import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import axios from "axios";
import MediaViewer from "../../components/mediaviewer";
import * as XLSX from "xlsx";
import { useTheme, alpha } from "@mui/material/styles";

/* ------------------------------------------------------------------ */
/* -------------------------- HELPERS --------------------------------*/
/* ------------------------------------------------------------------ */

// USD formatter
const formatCurrency = (v) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(v);

// Convert any date to a school-year string "YYYY-YYYY"
const getSchoolYear = (date) => {
    const d = new Date(date);
    const yr = d.getFullYear();
    const mo = d.getMonth(); // 0-Jan � 11-Dec
    return mo < 6 ? `${yr - 1}-${yr}` : `${yr}-${yr + 1}`;
};

// Give start / end JS Dates for a school-year string
const schoolYearToRange = (sy) => {
    const [startYr, endYr] = sy.split("-").map(Number);
    return {
        start: new Date(`${startYr}-07-01T00:00:00`),
        end: new Date(`${endYr}-06-30T23:59:59`),
    };
};

/* ------------------------------------------------------------------ */
/* --------------------------- ENDPOINTS -----------------------------*/
/* ------------------------------------------------------------------ */

const API_CATEGORIES =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_Categories";
const API_SUBCATEGORIES =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_Subcategories";
const API_EXPENSES =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_Expenses";

/* ------------------------------------------------------------------ */
/* ---------------------- MAIN COMPONENT -----------------------------*/
/* ------------------------------------------------------------------ */

const BudgetMonitoringPage = () => {
    const theme = useTheme();

    /* ------------------------------ STATE -------------------------- */
    const [categories, setCategories] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [expenses, setExpenses] = useState([]);

    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedSubcategory, setSelectedSubcategory] = useState("all");

    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [mediaImageUrl, setMediaImageUrl] = useState("");

    // School-year handling
    const currentSY = useMemo(() => getSchoolYear(Date.now()), []);
    const [selectedSchoolYear, setSelectedSchoolYear] = useState(currentSY);
    const [schoolYearOptions, setSchoolYearOptions] = useState([currentSY]);

    // Quick SY nav (Prev / Next)
    const syIndex = useMemo(
        () => schoolYearOptions.indexOf(selectedSchoolYear),
        [schoolYearOptions, selectedSchoolYear]
    );
    const canPrevSY = syIndex >= 0 && syIndex < schoolYearOptions.length - 1;
    const canNextSY = syIndex > 0;

    const goPrevSY = () => {
        if (!canPrevSY) return;
        const next = schoolYearOptions[syIndex + 1];
        setSelectedSchoolYear(next);
        setSelectedCategory("all");
        setSelectedSubcategory("all");
    };

    const goNextSY = () => {
        if (!canNextSY) return;
        const prev = schoolYearOptions[syIndex - 1];
        setSelectedSchoolYear(prev);
        setSelectedCategory("all");
        setSelectedSubcategory("all");
    };

    /* --------------------------- LOAD DATA ------------------------ */
    useEffect(() => {
        fetchCategories();
        fetchSubcategories();
        fetchExpenses();
    }, []);

    // 1) Categories
    const fetchCategories = async () => {
        try {
            const { data } = await axios.get(API_CATEGORIES);
            setCategories(
                data.filter(
                    (c) =>
                        c.Type === "Expense" &&
                        c.BudgetAmount != null &&
                        Number(c.BudgetAmount) > 0
                )
            );
        } catch (err) {
            console.error("Error loading categories:", err);
        }
    };

    // 2) Subcategories
    const fetchSubcategories = async () => {
        try {
            const { data } = await axios.get(API_SUBCATEGORIES);
            setSubcats(
                data.filter(
                    (s) =>
                        s.Type === "Expense" &&
                        s.BudgetAmount != null &&
                        Number(s.BudgetAmount) > 0
                )
            );
        } catch (err) {
            console.error("Error loading subcategories:", err);
        }
    };

    // 3) Expenses (also derive available school-years)
    const fetchExpenses = async () => {
        try {
            const { data } = await axios.get(API_EXPENSES);
            setExpenses(data);

            // Build unique SY list from data
            const years = Array.from(
                new Set(
                    data
                        .filter((e) => e.ExpenseDate)
                        .map((e) => getSchoolYear(e.ExpenseDate))
                )
            ).sort((a, b) => (a < b ? 1 : -1)); // latest first
            if (!years.includes(currentSY)) years.unshift(currentSY);
            setSchoolYearOptions(years);
        } catch (err) {
            console.error("Error loading expenses:", err);
        }
    };

    /* ---------------------- SCHOOL-YEAR FILTER -------------------- */
    const { start: syStart, end: syEnd } = useMemo(
        () => schoolYearToRange(selectedSchoolYear),
        [selectedSchoolYear]
    );

    const expensesThisSY = useMemo(
        () =>
            expenses.filter(
                (e) =>
                    e.ExpenseDate &&
                    new Date(e.ExpenseDate) >= syStart &&
                    new Date(e.ExpenseDate) <= syEnd
            ),
        [expenses, syStart, syEnd]
    );

    /* ----------------- EXPENSES LIMITED TO CATEGORIES ------------- */
    const allExpenseRecords = useMemo(
        () =>
            expensesThisSY.filter((e) =>
                categories.some((c) => String(c.CategoryID) === e.ExpenseCategoryID)
            ),
        [expensesThisSY, categories]
    );

    /* ----------------------- OVERALL USAGE ------------------------ */
    const overallBudget = categories.reduce(
        (s, c) => s + Number(c.BudgetAmount),
        0
    );
    const overallExpenses = allExpenseRecords.reduce(
        (s, e) => s + Number(e.Amount || 0),
        0
    );
    const overallPercent = overallBudget
        ? (overallExpenses / overallBudget) * 100
        : 0;

    /* ------------------------ CHART DATA -------------------------- */
    const categoryChartData = useMemo(
        () =>
            categories.map((c) => {
                const used = allExpenseRecords
                    .filter(
                        (e) => String(e.ExpenseCategoryID) === String(c.CategoryID)
                    )
                    .reduce((s, e) => s + Number(e.Amount || 0), 0);
                const pct = c.BudgetAmount
                    ? Math.min(100, (used / Number(c.BudgetAmount)) * 100)
                    : 0;
                return {
                    id: String(c.CategoryID),
                    label: c.CategoryName,
                    Target: 100,
                    Used: pct,
                };
            }),
        [categories, allExpenseRecords]
    );

    const subcategoryChartData = useMemo(
        () =>
            subcats.map((s) => {
                const used = allExpenseRecords
                    .filter((e) => e.ExpenseSubcategory === s.SubcategoryName)
                    .reduce((sum, e) => sum + Number(e.Amount || 0), 0);
                const pct = s.BudgetAmount
                    ? Math.min(100, (used / Number(s.BudgetAmount)) * 100)
                    : 0;
                return {
                    id: String(s.SubcategoryID),
                    label: s.SubcategoryName,
                    Target: 100,
                    Used: pct,
                };
            }),
        [subcats, allExpenseRecords]
    );

    /* ---------------- GRID FILTERS (CAT / SUBCAT) ----------------- */
    const filteredExpenses = useMemo(() => {
        if (selectedSubcategory !== "all") {
            const subName = subcats.find(
                (s) => String(s.SubcategoryID) === selectedSubcategory
            )?.SubcategoryName;
            return allExpenseRecords.filter(
                (e) => e.ExpenseSubcategory === subName
            );
        }
        if (selectedCategory !== "all") {
            return allExpenseRecords.filter(
                (e) => String(e.ExpenseCategoryID) === selectedCategory
            );
        }
        return allExpenseRecords;
    }, [selectedSubcategory, selectedCategory, allExpenseRecords, subcats]);

    /* --------------------- STAT BAR (TOP) ------------------------- */
    let statPct = overallPercent;
    let statName = "Overall";
    if (selectedSubcategory !== "all") {
        const row = subcategoryChartData.find((r) => r.id === selectedSubcategory);
        statPct = row ? row.Used : 0;
        statName = row ? row.label : "Selected Subcategory";
    } else if (selectedCategory !== "all") {
        const row = categoryChartData.find((r) => r.id === selectedCategory);
        statPct = row ? row.Used : 0;
        statName = row ? row.label : "Selected Category";
    }

    /* ------------------------- COLORS ----------------------------- */
    const green = theme.palette.greenAccent?.[500] || "#77DD77";
    const red = theme.palette.redAccent?.[500] || "#FF6961";

    /* ----------------------- EXCEL EXPORT ------------------------- */
    const handleDownloadExcel = () => {
        const data = [
            ["Invoice #", "Date", "Amount", "Category", "Subcategory", "Desc"],
        ];
        filteredExpenses.forEach((e) => {
            const date = e.ExpenseDate
                ? new Date(e.ExpenseDate).toLocaleDateString()
                : "";
            const cat = categories.find(
                (c) => String(c.CategoryID) === e.ExpenseCategoryID
            );
            data.push([
                e.InvoiceNumber || "",
                date,
                formatCurrency(e.Amount || 0),
                cat?.CategoryName || "",
                e.ExpenseSubcategory || "",
                e.Description || "",
            ]);
        });
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, "Expenses");
        XLSX.writeFile(wb, `BudgetExpenses_${selectedSchoolYear}.xlsx`);
    };

    /* ------------------------- GRID COLS -------------------------- */
    const columns = [
        {
            field: "viewInvoice",
            headerName: "View Invoice",
            width: 140,
            renderCell: (p) => (
                <Button
                    variant="contained"
                    sx={{ backgroundColor: green, color: "#fff" }}
                    onClick={() => {
                        setMediaImageUrl(p.row.imageURL);
                        setMediaViewerOpen(true);
                    }}
                >
                    View Invoice
                </Button>
            ),
        },
        { field: "InvoiceNumber", headerName: "Invoice #", width: 120 },
        {
            field: "ExpenseDate",
            headerName: "Date",
            width: 110,
            valueGetter: (p) =>
                p.row.ExpenseDate
                    ? new Date(p.row.ExpenseDate).toLocaleDateString()
                    : "",
        },
        {
            field: "Amount",
            headerName: "Amount",
            width: 110,
            valueFormatter: (p) => formatCurrency(p.value || 0),
        },
        {
            field: "ExpenseCategory",
            headerName: "Category",
            width: 130,
            valueGetter: (p) => {
                const c = categories.find(
                    (c) => String(c.CategoryID) === p.row.ExpenseCategoryID
                );
                return c?.CategoryName || "";
            },
        },
        {
            field: "ExpenseSubcategory",
            headerName: "Subcategory",
            width: 130,
            valueGetter: (p) => p.row.ExpenseSubcategory || "N/A",
        },
        { field: "Description", headerName: "Description", flex: 1 },
    ];

    /* --------------------------- RENDER --------------------------- */
    return (
        <Box p={2}>
            {/* ---------------- SCHOOL-YEAR PICKER ---------------- */}
            <Box
                mb={3}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
            >
                <Box
                    p={2}
                    mb={1}
                    sx={{
                        bgcolor: theme.palette.background.default,
                        borderRadius: 2,
                        textAlign: "center",
                        flexGrow: 1,
                    }}
                >
                    <Typography variant="h6">
                        {statName} Usage ({selectedSchoolYear}): {statPct.toFixed(2)}%
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" ml={2} gap={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!canPrevSY}
                        onClick={goPrevSY}
                    >
                        Prev SY
                    </Button>

                    <FormControl sx={{ minWidth: 160 }} size="small">
                        <InputLabel>School Year</InputLabel>
                        <Select
                            value={selectedSchoolYear}
                            label="School Year"
                            onChange={(e) => {
                                setSelectedSchoolYear(e.target.value);
                                setSelectedCategory("all");
                                setSelectedSubcategory("all");
                            }}
                        >
                            {schoolYearOptions.map((sy) => (
                                <MenuItem key={sy} value={sy}>
                                    {sy}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!canNextSY}
                        onClick={goNextSY}
                    >
                        Next SY
                    </Button>
                </Box>
            </Box>

            <Typography variant="h4" gutterBottom>
                District Budget Monitoring
            </Typography>

            {/* ---------------- CATEGORY CHART ------------------- */}
            <Box mb={4} height={350}>
                <ResponsiveContainer>
                    <BarChart
                        data={categoryChartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        onClick={(d) => {
                            if (d.activePayload?.length) {
                                setSelectedCategory(d.activePayload[0].payload.id);
                                setSelectedSubcategory("all");
                            }
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <RechartsTooltip formatter={(v) => `${v.toFixed(2)}%`} />
                        <Legend />
                        <Bar dataKey="Target" name="Goal (100%)" fill={green} />
                        <Bar dataKey="Used" name="% Used" fill={red} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>

            {/* ---------------- SUBCATEGORY CHART ---------------- */}
            <Typography variant="h6" sx={{ mb: 1 }}>
                Subcategory Usage
            </Typography>
            <Box mb={4} height={300}>
                <ResponsiveContainer>
                    <BarChart
                        data={subcategoryChartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        onClick={(d) => {
                            if (d.activePayload?.length) {
                                setSelectedSubcategory(d.activePayload[0].payload.id);
                                setSelectedCategory("all");
                            }
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <RechartsTooltip formatter={(v) => `${v.toFixed(2)}%`} />
                        <Legend />
                        <Bar dataKey="Target" name="Goal (100%)" fill={green} />
                        <Bar dataKey="Used" name="% Used" fill={red} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>

            {/* ----------------- DOWNLOAD & GRID ----------------- */}
            <Box mb={2}>
                <Button
                    variant="contained"
                    sx={{
                        bgcolor: theme.palette.jefferson?.main || "#FFD700",
                        color: "#000",
                        fontWeight: "bold",
                    }}
                    onClick={handleDownloadExcel}
                >
                    <img
                        src="https://static.vecteezy.com/system/resources/previews/022/100/783/non_2x/microsoft-excel-logo-transparent-free-png.png"
                        alt="Excel"
                        style={{ height: 20, marginRight: 8 }}
                    />
                    Download Excel
                </Button>
            </Box>

            <Box mb={4} sx={{ height: 400 }}>
                <DataGrid
                    rows={filteredExpenses}
                    columns={columns}
                    getRowId={(r) => r.apiid}
                    pageSizeOptions={[10, 20, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    sx={{
                        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? alpha(theme.palette.background.paper, 0.4)
                                    : alpha(theme.palette.background.paper, 0.9),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        // simple, theme-aware zebra striping
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? alpha(theme.palette.primary.main, 0.10)
                                    : alpha(theme.palette.primary.light, 0.08),
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? alpha(theme.palette.primary.light, 0.15)
                                    : alpha(theme.palette.primary.main, 0.04),
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                    }}
                />
            </Box>

            <Box textAlign="right" fontSize="1.2rem">
                Total Expenses:{" "}
                {formatCurrency(
                    filteredExpenses.reduce((s, e) => s + Number(e.Amount || 0), 0)
                )}
            </Box>

            {mediaViewerOpen && (
                <MediaViewer
                    open={mediaViewerOpen}
                    src={mediaImageUrl}
                    onClose={() => setMediaViewerOpen(false)}
                    type="image"
                />
            )}
        </Box>
    );
};

export default BudgetMonitoringPage;
